import { getFirstNameToken } from './userName';

const PROFILE_SUMMARY_TTL_MS = 60000;

const cache = {
  userType: null,
  firstName: '',
  avatarUrl: null,
  fetchedAt: 0,
};

let inFlight = null;
let inFlightSeq = 0;

function getAvatarUrl(userType, data) {
  if (userType === 'brand') {
    return data?.brand_logo_url || null;
  }
  if (userType === 'influencer') {
    return data?.profile_picture_url || null;
  }
  return null;
}

function getProfilePath(userType) {
  if (userType === 'brand') return '/api/brand/profile';
  if (userType === 'influencer') return '/api/influencer/profile';
  return null;
}

/**
 * Shared profile summary for landing/header (first name + avatar).
 * Dedupes in-flight requests and caches for PROFILE_SUMMARY_TTL_MS.
 */
export async function fetchProfileSummary(userType, { force = false } = {}) {
  if (userType !== 'brand' && userType !== 'influencer') {
    return { firstName: '', avatarUrl: null };
  }

  const now = Date.now();
  if (
    !force
    && cache.userType === userType
    && cache.fetchedAt
    && now - cache.fetchedAt < PROFILE_SUMMARY_TTL_MS
  ) {
    return { firstName: cache.firstName, avatarUrl: cache.avatarUrl };
  }

  if (inFlight && inFlight.userType === userType) {
    const summary = await inFlight.promise;
    return summary;
  }

  const token = localStorage.getItem('token');
  if (!token) {
    return { firstName: '', avatarUrl: null };
  }

  const path = getProfilePath(userType);
  const requestId = inFlightSeq + 1;
  inFlightSeq = requestId;
  const promise = (async () => {
    const response = await fetch(`${process.env.REACT_APP_API_URL}${path}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch ${path}`);
    }

    const data = await response.json();
    const summary = {
      firstName: getFirstNameToken(data.full_name),
      avatarUrl: getAvatarUrl(userType, data),
    };

    cache.userType = userType;
    cache.firstName = summary.firstName;
    cache.avatarUrl = summary.avatarUrl;
    cache.fetchedAt = Date.now();

    return summary;
  })();

  inFlight = { userType, promise, requestId };

  try {
    return await promise;
  } finally {
    if (inFlight?.requestId === requestId) {
      inFlight = null;
    }
  }
}

/** Resets module cache (tests / logout). */
export function resetProfileSummaryCache() {
  cache.userType = null;
  cache.firstName = '';
  cache.avatarUrl = null;
  cache.fetchedAt = 0;
  inFlight = null;
}
